import {
  FEATURE_FLAG_BRAND,
  type FeatureFlagDefinitionMap,
  type FeatureFlagHandle,
  type FeatureFlagHandles,
  type FeatureFlagProblem,
  type StringEnum,
} from './types';

/** Mutable while being wired up; frozen before it leaves this module. */
type DraftHandle = {
  id: string;
  discoveryKeys: readonly FeatureFlagHandle['discoveryKeys'][number][];
  parent?: DraftHandle;
  problem?: FeatureFlagProblem;
  [FEATURE_FLAG_BRAND]: true;
};

/**
 * Walks up from `flag`; a handle reachable from itself is on a cycle. Checked
 * once here so `evaluateFeatureFlags` can follow the parent chain without
 * guarding against an infinite loop on every call.
 */
const isOnCycle = (flag: DraftHandle): boolean => {
  const seen = new Set<DraftHandle>();

  for (let current = flag.parent; current; current = current.parent) {
    if (current === flag) return true;
    if (seen.has(current)) return false;
    seen.add(current);
  }

  return false;
};

/**
 * Turns a feature-owned string enum and its definitions into handles keyed by
 * enum member name. The definition map is exhaustive, so adding an enum member
 * without its parent/discovery metadata is a type error.
 *
 * Structural problems — duplicate ids, a parent that does not exist, cyclic
 * parents — are found here, once, and recorded on the handle. Evaluation then
 * fails closed on them without re-deriving anything. This runs once per module
 * load; evaluation runs on every render, so the work belongs on this side.
 *
 * A problem is recorded rather than thrown: a `throw` here happens at import
 * time and takes down the whole application over one feature's typo. Recording
 * it denies access to that feature, logs which one and why in development, and
 * leaves the rest of the app running — the same fail-closed answer the flag
 * being off would give.
 */
export const defineFeatureFlags = <const Enum extends StringEnum>(
  enumValues: Enum,
  definitions: FeatureFlagDefinitionMap<NoInfer<Enum>>
): FeatureFlagHandles<Enum> => {
  // Enum member name -> handle, so call sites read `Gate.ActionsEnabled` rather
  // than repeating the wire id, which then only appears in the enum.
  const memberNames = Object.keys(enumValues) as Array<Extract<keyof Enum, string>>;
  const draftsById = new Map<string, DraftHandle>();
  const drafts = memberNames.map((memberName) => {
    const id = enumValues[memberName];
    const definition = definitions[memberName];
    const draft: DraftHandle = {
      id,
      discoveryKeys: [...(definition.discoveryKeys ?? [])],
      [FEATURE_FLAG_BRAND]: true,
    };

    if (draftsById.has(id)) {
      draft.problem = {
        reason: 'unknown',
        flagId: id,
        message: `Unknown or duplicate feature flag "${id}". Access denied.`,
      };
    }
    draftsById.set(id, draft);

    return { memberName, draft, parentId: definition.parent as string | undefined };
  });

  // Second pass: every draft exists by now, so a parent declared before or after
  // its child resolves the same way and definition order never matters.
  drafts.forEach(({ draft, parentId }) => {
    if (!parentId) return;

    const parent = draftsById.get(parentId);
    if (!parent) {
      draft.problem ??= {
        reason: 'unknown',
        flagId: parentId,
        message: `Unknown parent "${parentId}" for feature flag "${draft.id}". Access denied.`,
      };
      return;
    }

    draft.parent = parent;
  });

  drafts.forEach(({ draft }) => {
    if (draft.problem || !isOnCycle(draft)) return;

    draft.problem = {
      reason: 'cycle',
      flagId: draft.id,
      message: `Cyclic feature flag parent relationship at "${draft.id}". Access denied.`,
    };
  });

  // Frozen because handles are module-level singletons shared by every consumer:
  // evaluation compares them by reference, so a mutated handle would silently
  // change the answer everywhere.
  const handles: Partial<Record<keyof Enum, FeatureFlagHandle>> = {};
  drafts.forEach(({ memberName, draft }) => {
    Object.freeze(draft.discoveryKeys);
    handles[memberName] = Object.freeze(draft) as FeatureFlagHandle;
  });

  return Object.freeze(handles) as FeatureFlagHandles<Enum>;
};
